import styled from '@emotion/styled';
import { useState } from 'react';

import { useAuth } from '@/services/auth';

import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { LogoUpload } from '@/components/LogoUpload/LogoUpload';
import { getThumbnailFromFile } from '@/components/LogoUpload/utils';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { pricingStudioUrl, salesUrl } from '@/environment';
import ChatFigures from '@/images/ChatFigures.svg';
import dump from '@/images/dump.svg';
import FlashLightning2 from '@/images/FlashLightning2.svg';
import { useAnalytics } from '@/services/analytics';
import { useDeleteLogo, useGetLogo, useUploadLogo } from '@/state/user-settings/userSettings';
import { StyleCollection } from '@/utils/types';
import { PlanGroup } from '@d-id/types';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, DialogTitle, Typography } from '@mui/material';
import Gallery from '../../../../images/Gallery.svg';
import BannerBackground from '../../../../images/UploadLogoBackground.png';

const Logo = styled.div<{ src: string }>`
    width: 72px;
    height: 72px;
    object-fit: none;
    border-radius: 100%;
    background-image: url(${props => props.src});
    background-repeat: no-repeat;
    background-size: cover;

    div {
        display: none;
    }

    :hover div {
        display: flex;
        cursor: pointer;
    }
`;

const LogoRemove = styled.div`
    background-color: white;
    opacity: 0.9;
    width: 72px;
    height: 72px;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    h5 {
        font-weight: 600 !important;
    }
`;

const LogoPlaceholder = styled.img`
    width: 72px;
    height: 72px;
    object-fit: none;
    border-radius: 100%;
    background-color: ${({ theme }) => theme.colors.black[50]};

    :hover {
        cursor: pointer;
    }
`;

const UpgradePlanBanner = styled.div<{ src: string }>`
    background-image: url(${props => props.src});
    height: 166px;
    border-radius: 12px;
    margin-top: 8px;
    padding: 16px 24px;
`;

const WhiteText = styled(Typography)`
    color: white;
    margin-bottom: 8px;
    line-height: inherit;
`;

const WhiteButton = styled(Button)`
    background-color: white;
    color: ${({ theme }) => theme.colors.secondary[900]};
    text-transform: none;
    padding: 10px 16px;
    margin-top: 16px;

    &.MuiButton-contained:hover {
        background-color: ${({ theme }) => theme.colors.black[100]} !important;
    }

    svg {
        margin-right: 6px;
    }
`;

const StyledH4 = styled(Typography)`
    margin: 16px 0 8px;
    display: flex;
    align-items: center;

    svg {
        margin-left: 8px;
        color: ${({ theme }) => theme.colors.primary[900]};
    }
`;

const SaveButton = styled(Button)`
    margin-top: 16px;
    margin-left: auto;
    padding: 10px 16px;
    text-transform: none;
    border-radius: 8px;
    width: 100px;
`;

const TextLight = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[700]};
`;

const TextOrange = styled(Typography)`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.primary[900]};
    font-weight: 600;
`;

const SectionContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    align-items: center;
    cursor: pointer;

    &:hover ${TextOrange} {
        color: ${({ theme }) => theme.colors.primary[950]};
    }
`;

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
`;

const styles: StyleCollection = {
    dialogTitle: {
        height: '5rem',
        fontSize: '1.6rem',
    },
    closeIcon: theme => ({
        position: 'absolute',
        top: 16,
        right: 16,
        color: theme.colors.black.main,
    }),
    rootStyle: {
        padding: '16px',
        borderRadius: '12px',
    },
};

const getImageDimension = async (file: File): Promise<{ width: number; height: number }> => {
    const fileSrc = await getThumbnailFromFile(file);

    return new Promise(resolve => {
        const image = new Image();

        image.src = fileSrc;

        image.onload = () => {
            resolve({ width: image.width, height: image.height });
        };
    });
};

export const UploadLogo = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const [modalState, setModalState] = useState(false);
    const [file, setFile] = useState<File | undefined>(undefined);

    const { mutateAsync: uploadLogo, isLoading: uploadLoading } = useUploadLogo();
    const { mutateAsync: deleteLogo } = useDeleteLogo();
    const { data } = useGetLogo();
    const mixpanel = useAnalytics();

    const logo = data?.url;

    const isLogoAvailable =
        (user?.features || []).includes('logo') ||
        user?.planGroup === PlanGroup.SCALE ||
        user?.planGroup === PlanGroup.ADVANCED ||
        user?.planGroup === PlanGroup.ENTERPRISE;

    const handleClose = () => {
        setModalState(false);
        setFile(undefined);
    };

    const handleOpen = () => {
        if (!isLogoAvailable) {
            mixpanel?.track('upgrade-package', { event: 'view', from: 'upload-logo' });
        }
        setModalState(true);
    };

    const handleUpgradeClick = () => {
        mixpanel?.track('upgrade-package', { event: 'click', from: 'upload-logo' });
        window.open(pricingStudioUrl, '_blank');
    };

    const handleSave = async () => {
        if (file) {
            const dimensions = await getImageDimension(file);
            await uploadLogo({ file, top: -(dimensions.height + 24), left: 24 });
        }
        handleClose();
    };

    const handleDelete = async () => {
        await deleteLogo();
    };

    const handleTalkToSales = () => {
        mixpanel?.track('upgrade-package', { event: 'click', from: 'upload-logo' });
        window.open(salesUrl, '_blank');
    };

    return (
        <>
            {logo ? (
                <SectionContainer>
                    <Logo src={logo}>
                        <LogoRemove onClick={handleDelete}>
                            <MaskedIcon src={dump} size="16px" />
                            <Typography variant="h5">Remove</Typography>
                        </LogoRemove>
                    </Logo>
                    <TextContainer onClick={handleOpen}>
                        <TextOrange variant="h4">Change logo</TextOrange>
                        <TextLight variant="body2">JPG/PNG up to 5MB</TextLight>
                    </TextContainer>
                </SectionContainer>
            ) : (
                <SectionContainer onClick={handleOpen}>
                    <LogoPlaceholder src={Gallery} color={theme.colors.black[500]} />
                    <TextContainer>
                        <TextOrange variant="h4">Upload logo</TextOrange>
                        <TextLight variant="body2">JPG/PNG up to 5MB</TextLight>
                    </TextContainer>
                </SectionContainer>
            )}

            <BootstrapDialog onClose={handleClose} open={modalState} rootstyle={styles.rootStyle} width="67rem">
                <IconButton ariaLabel="close" onClick={handleClose} sx={styles.closeIcon}>
                    <CloseIcon />
                </IconButton>
                <DialogTitle sx={styles.dialogTitle}>
                    <Typography variant="h3">Company logo</Typography>
                </DialogTitle>
                <DialogContent>
                    {!isLogoAvailable && (
                        <UpgradePlanBanner src={BannerBackground}>
                            <WhiteText variant="h3">Keep your videos on brand</WhiteText>
                            <WhiteText variant="body2">
                                Apply your own logo to your videos. <br />
                                This feature is accessible to users on the Advanced and Enterprise plans.
                            </WhiteText>
                            <WhiteButton id="talk-to-sales-logo" onClick={handleTalkToSales}>
                                <MaskedIcon src={ChatFigures} size="24px" />
                                Contact Sales
                            </WhiteButton>
                        </UpgradePlanBanner>
                    )}

                    <StyledH4 variant="h4">
                        Upload
                        {!isLogoAvailable && <MaskedIcon src={FlashLightning2} size="16px" />}
                    </StyledH4>
                    <LogoUpload file={file} onChange={setFile} disabled={!isLogoAvailable} onClickUpgrade={handleUpgradeClick} />
                    <SaveButton variant="contained" color="secondary" isLoading={uploadLoading} onClick={file ? handleSave : handleClose}>
                        Save
                    </SaveButton>
                </DialogContent>
            </BootstrapDialog>
        </>
    );
};
